import { OrderResponseDto, OrderStatus } from '@planning/app/api'
import { GeneralCargoOrderLocationItem } from '@planning/pages/GeneralCargo/Components/GeneralCargoOrderLocationItem'
import { useCallback, useEffect, useState } from 'react'

export const useGeneralCargoLocations = (
  orders: OrderResponseDto[],
  getOrderLocationsFunc?: (
    orderIds: number[],
  ) => Promise<Array<{ orderId: number; locations: GeneralCargoOrderLocationItem[] }>>,
  getOrderReservationsFunc?: (
    orderIds: number[],
  ) => Promise<Array<{ orderId: number; planned: GeneralCargoOrderLocationItem[] }>>,
  reservationUpdate?: { orderId: number; reservations: GeneralCargoOrderLocationItem[] },
) => {
  const [orderLocationsById, setOrderLocationsById] = useState<
    Record<number, GeneralCargoOrderLocationItem[]>
  >({})

  const [orderReservationsById, setOrderReservationsById] = useState<
    Record<number, GeneralCargoOrderLocationItem[]>
  >({})

  const handleGetOrderReservations = useCallback(
    async (orderIds: number[]) => {
      if (!getOrderReservationsFunc) return []

      const reservations = await getOrderReservationsFunc(orderIds)

      return reservations?.map(entry => ({
        orderId: entry.orderId,
        reservations: entry.planned?.map(item => ({
          generalCargoLocation: item.generalCargoLocation,
          generalCargoArea: item.generalCargoArea,
          quantity: item.quantity,
        })),
      }))
    },
    [getOrderReservationsFunc],
  )

  useEffect(() => {
    if (!getOrderLocationsFunc) {
      return
    }

    const orderIds = orders.filter(o => o.status === OrderStatus.Fulfilled).map(order => order.id)

    if (!orderIds.length) {
      setOrderLocationsById({})
      return
    }

    let isActive = true

    getOrderLocationsFunc(orderIds)
      .then(locations => {
        if (!isActive) {
          return
        }

        const locationsById = locations.reduce(
          (acc, entry) => {
            acc[entry.orderId] = entry.locations
            return acc
          },
          {} as Record<number, GeneralCargoOrderLocationItem[]>,
        )

        setOrderLocationsById(locationsById)
      })
      .catch(() => {
        if (isActive) {
          setOrderLocationsById({})
        }
      })

    return () => {
      isActive = false
    }
  }, [orders, getOrderLocationsFunc])

  useEffect(() => {
    if (!handleGetOrderReservations) {
      return
    }

    const orderIds = orders.map(order => order.id)

    if (!orderIds.length) {
      setOrderReservationsById({})
      return
    }

    let isActive = true

    handleGetOrderReservations(orderIds)
      .then(reservations => {
        if (!isActive) {
          return
        }

        const reservationsById = reservations.reduce(
          (acc, entry) => {
            acc[entry.orderId] = entry.reservations
            return acc
          },
          {} as Record<number, GeneralCargoOrderLocationItem[]>,
        )

        setOrderReservationsById(reservationsById)
      })
      .catch(error => {
        if (isActive) {
          setOrderReservationsById({})
        }
      })

    return () => {
      isActive = false
    }
  }, [orders, handleGetOrderReservations])

  useEffect(() => {
    if (!reservationUpdate) {
      return
    }

    setOrderReservationsById(prev => ({
      ...prev,
      [reservationUpdate.orderId]: reservationUpdate.reservations,
    }))
  }, [reservationUpdate])

  return {
    orderLocationsById,
    orderReservationsById,
  }
}
